import { useSelector } from 'react-redux';
import findResult, { findItemByID } from '@/utils/resultsHelper';
import { readStates, states } from '@/constants/lifecycle';
import { firstError } from '@/utils/errors';
import AttachmentPolicy from '../policies/attachmentPolicy';
import datasetStatusInitialState from '../../status/state/state';
import { datasetPerms } from '../../sharing/state/state';
import {
  computeBreadcrumbIsRoot,
  computeBreadcrumbSegments,
  filterAttachments,
  resolveCurrentPath,
  sortAttachments,
} from '../helper';
import attachmentsInitialState from '../state/state';
import { resolveCatalogueTemplate } from '../../catalogue/helper';
import { datasetDirtyFlags, datasetSourceAndStepAttributes, isDatasetWizardOn } from '../../state/state';
import AttachmentsPolicy from '../policies/attachmentsPolicy';
import { dictionariesHaveChanged } from '../../dictionaries/helper';

const useAddDatasetAttachments = ({ category, code }) => useSelector(({
  auth: { role },
  datasets: { datasets },
  catalogues: { templates },
  datasetDatasource: { features },
}) => {
  const { dataset, datasourceFeatures } = resolveCatalogueTemplate(datasets, templates, { category, code }, {
    attachments: attachmentsInitialState(),
    copy: { dictionaries: [] },
    dictionaries: [],
    isDirty: datasetDirtyFlags(),
    permissions: datasetPerms(),
    status: datasetStatusInitialState(),
  }, features);
  const policy = new AttachmentsPolicy(role, dataset);

  return {
    canConvert: policy.canConvert(datasourceFeatures),
    isDirty: dictionariesHaveChanged(dataset),
    readStatus: datasourceFeatures.readStatus,
  };
});

const useAssignDataToDictionaryDialog = () => useSelector(({
  auth: { role },
  datasets: {
    activeDictionary: {
      category,
      code,
      dictionaryFromDatafile,
      saveStatus,
      saveErrors,
      attachment,
    }, datasets,
  },
}) => {
  const dataset = findResult(datasets, category, code);
  const dictionaryDatafileItems = dataset ? dictionaryFromDatafile.items : [];
  const dictionaries = dataset ? dataset.dictionaries : [];
  const filterValue = dataset ? dictionaryFromDatafile.filter : '';
  const deleteSource = dataset ? dictionaryFromDatafile.deleteSource : false;

  return {
    attachmentErrors: dataset ? dictionaryFromDatafile.readErrors : [],
    attachmentStatus: dataset ? dictionaryFromDatafile.readStatus : states.NONE,
    attachmentId: dataset ? attachment?.id : null,
    canDelete: dataset ? new AttachmentsPolicy(role, dataset).canDelete : false,
    category,
    code,
    deleteSource,
    dictionaries,
    dictionaryFromDatafile: dictionaryDatafileItems[0],
    filterValue,
    saveDictionaryErrors: dataset ? saveErrors : [],
    saveDictionaryStatus: dataset ? saveStatus : states.NONE,
  };
});

const useAttachmentToolsMenu = ({ category, code, attachment }) => useSelector(({
  auth: { role },
  catalogues: { templates },
  datasets: { datasets },
  datasetDatasource: { features },
}) => {
  const { dataset, datasourceFeatures } = resolveCatalogueTemplate(
    datasets,
    templates,
    { category, code },
    undefined,
    features,
  );
  const policy = new AttachmentPolicy(role, dataset, attachment);

  return {
    canDelete: policy.canDelete,
    canDownload: policy.canDownload,
    canIngest: policy.canIngest(datasourceFeatures),
    canUpdate: policy.canUpdate,
    isLoading: dataset.attachments.updatingItems[attachment.id],
    isRoot: computeBreadcrumbIsRoot(dataset.attachments.folders),
  };
});

const useDatasetAttachmentDeletionDialog = () => useSelector(({
  datasets: { datasets: { attachmentsDeletion: { results } } },
}) => ({ results }));

const useDatasetAttachmentDialogs = () => useSelector(({
  auth: {
    role: {
      permissions: {
        attachments: {
          create, destroy, ingest, update,
        },
      },
    },
  },
  datasets: {
    activeAttachment,
    activeFolder,
    createFolder,
    deleteDatasetAttachmentOpen,
    activeDictionary,
    datasets: { downloadableAttachmentConfirmation },
  },
}) => ({
  allowCreate: downloadableAttachmentConfirmation.open && create,
  allowCreateFolder: createFolder.open && create,
  allowDelete: deleteDatasetAttachmentOpen && destroy,
  allowDeleteFolder: activeFolder.open && destroy,
  allowIngest: activeDictionary.open && ingest,
  allowUpdate: activeAttachment.open && update,
}));

const useDatasetAttachmentEditDialog = () => useSelector(({
  datasets: { activeAttachment },
}) => ({
  attachment: activeAttachment.attachment,
  category: activeAttachment.category,
  code: activeAttachment.code,
  isDirty: activeAttachment.isDirty,
  updateErrors: activeAttachment.updateErrors,
  updateStatus: activeAttachment.updateStatus,
}));

const extracted = (role, datasets, templates, category, code, attachment) => {
  const { dataset } = resolveCatalogueTemplate(datasets, templates, { category, code });
  const policy = new AttachmentPolicy(role, dataset, attachment);

  return {
    dataset,
    state: {
      canDownload: policy.canDownload,
      canUpdate: policy.canUpdate,
      isDownloading: !!findItemByID(dataset.attachments.downloadsInProgress, attachment),
      isLoading: dataset.attachments.updatingItems[attachment.id],
      isRoot: computeBreadcrumbIsRoot(dataset.attachments.folders),
    },
  };
};

const useDatasetAttachmentCardItem = ({ category, code, attachment }) => useSelector(({
  auth: { role },
  catalogues: { templates },
  datasets: { datasets },
}) => extracted(role, datasets, templates, category, code, attachment).state);

const useDatasetAttachmentRow = ({ category, code, attachment }) => useSelector(({
  auth: { role },
  catalogues: { templates },
  datasets: { datasets },
}) => {
  const { dataset, state } = extracted(role, datasets, templates, category, code, attachment);
  return { ...state, isEditing: dataset.isEditing && !isDatasetWizardOn(dataset, role) };
});

const datasetAttachmentsState = (datasets, templates, role, features, category, code, initialSegment) => {
  const { dataset, exists } = resolveCatalogueTemplate(datasets, templates, { category, code }, {
    asViewer: { on: false },
    attachments: {
      filters: {},
      items: [],
      ...readStates,
      folders: { currentPathSegments: [] },
      orderBy: [],
    },
    catalogue: { title: '' },
    isEditing: false,
    steps: datasetSourceAndStepAttributes(),
    status: datasetStatusInitialState(),
    permissions: datasetPerms(),
  }, features);
  const policy = new AttachmentsPolicy(role, dataset);
  const {
    filters, orderBy,
  } = dataset.attachments;
  const {
    folders, items, readStatus, readErrors,
  } = dataset.attachments;
  const path = resolveCurrentPath(folders.currentPathSegments);
  const { segments, isRoot } = computeBreadcrumbSegments(initialSegment, folders);

  return {
    dataset,
    policy,
    state: {
      canUpdate: policy.canUpdate,
      exists,
      items: sortAttachments(filterAttachments(items, filters, null, !dataset.asViewer.on), orderBy),
      name: dataset.catalogue.title,
      readStatus,
      readErrors,
      path,
      segments,
      isRoot,
    },
  };
};

const useDatasetAttachmentsCards = ({ category, code }) => useSelector(({
  auth: { role },
  catalogues: { templates },
  datasets: { datasets },
  datasetDatasource: { features },
}) => {
  const { policy, state } = datasetAttachmentsState(
    datasets,
    templates,
    role,
    features,
    category,
    code,
    { icon: 'folder open outline' },
  );
  const storageEnabled = policy.storageEnabled && !policy.storageError;
  return { ...state, canUpload: policy.canUpload, storageEnabled };
});

const useDatasetAttachmentsTable = ({ category, code }) => useSelector(({
  auth: { role },
  datasets: { datasets },
  catalogues: { templates },
  datasetDatasource: { features },
}) => {
  const { dataset, state } = datasetAttachmentsState(datasets, templates, role, features, category, code);
  const wizardOn = isDatasetWizardOn(dataset, role);
  return { ...state, isEditing: dataset.isEditing && !wizardOn, wizardOn };
});

const useDatasetAttachmentsTableTitle = ({ category, code }) => useSelector(({
  datasets: { datasets },
  catalogues: { templates },
}) => {
  const { dataset } = resolveCatalogueTemplate(datasets, templates, { category, code });
  const { segments } = computeBreadcrumbSegments({ messageId: 'dataset.attachments' }, dataset.attachments.folders);
  return { segments };
});

const useDatasetAttachmentsFilters = ({ category, code }) => useSelector(({
  catalogues: { templates },
  datasets: { datasets },
}) => {
  const { dataset } = resolveCatalogueTemplate(datasets, templates, { category, code }, {
    attachments: { filters: { byTextInput: '' } },
  });
  const { byTextInput, byType } = dataset.attachments.filters;
  return { filterTerm: byTextInput, typeFilter: byType };
});

const useDatasetAttachmentsTableBody = ({ category, code }) => useSelector(({
  auth: { role },
  catalogues: { templates },
  datasets: { datasets },
}) => {
  const { dataset } = resolveCatalogueTemplate(datasets, templates, { category, code });
  const policy = new AttachmentsPolicy(role, dataset);
  const { readStatus, readErrors } = dataset.attachments;

  return {
    error: policy.storageError,
    readStatus,
    readError: firstError(readErrors),
    storageEnabled: policy.storageEnabled,
  };
});

const useDatasetAttachmentsTableHeader = ({ category, code }) => useSelector(({
  auth: { role },
  datasets: { datasets },
  catalogues: { templates },
  datasetDatasource: { features },
}) => {
  const { dataset } = resolveCatalogueTemplate(datasets, templates, { category, code }, {
    asViewer: { on: false },
    attachments: {
      items: [],
      ...readStates,
      folders: { currentPathSegments: [] },
      orderBy: [],
    },
    isEditing: false,
    status: datasetStatusInitialState(),
    permissions: datasetPerms(),
  }, features);
  const policy = new AttachmentsPolicy(role, dataset);
  const { readStatus, readErrors } = dataset.attachments;

  return {
    canUpload: policy.canUpload,
    canUpdate: policy.canUpdate,
    canManage: policy.canManage,
    orderBy: dataset.attachments.orderBy,
    readStatus,
    readErrors,
    storageEnabled: policy.storageEnabled && !policy.storageError,
  };
});

const useDownloadableDatafileConfirmationDialog = () => useSelector(({
  datasets: {
    datasets: {
      downloadableAttachmentConfirmation: {
        attachmentId, category, code, isFolder,
      },
    },
  },
}) => ({
  attachmentId, category, code, isFolder,
}));

const useDatasetFolderDeletionDialog = () => useSelector(({
  datasets: { activeFolder: { folder } },
}) => ({ folder }));

const useDatasetFolderCreationDialog = () => useSelector(({
  datasets: {
    createFolder: {
      open, category, code, saveStatus, saveErrors, name,
    },
  },
}) => ({
  open, category, code, saveStatus, saveErrors, name,
}));

export default {
  useAddDatasetAttachments,
  useAssignDataToDictionaryDialog,
  useAttachmentToolsMenu,
  useDatasetAttachmentCardItem,
  useDatasetAttachmentDeletionDialog,
  useDatasetAttachmentDialogs,
  useDatasetAttachmentEditDialog,
  useDatasetAttachmentRow,
  useDatasetAttachmentsCards,
  useDatasetAttachmentsFilters,
  useDatasetAttachmentsTable,
  useDatasetAttachmentsTableBody,
  useDatasetAttachmentsTableHeader,
  useDatasetAttachmentsTableTitle,
  useDatasetFolderCreationDialog,
  useDatasetFolderDeletionDialog,
  useDownloadableDatafileConfirmationDialog,
};
